/**
 * HTTP Service
 *
 * Provides a pre-configured Axios instance for making HTTP requests.
 * This service is used across the application for API calls.
 *
 * @example
 * ```typescript
 * import { http } from '@/services/http'
 *
 * // GET request
 * const response = await http.get('/api/endpoint')
 *
 * // POST request
 * const response = await http.post('/api/endpoint', { data: 'value' })
 *
 * // With custom config
 * const response = await http.get('/api/endpoint', { timeout: 5000 })
 * ```
 */

import axios from 'axios'

/**
 * Pre-configured Axios instance with:
 * - 10 second timeout
 * - JSON content-type header
 * - HTTP/HTTPS agents with keep-alive enabled (server-side only)
 * - Connection pooling for better performance
 */
const createHttpClient = (): ReturnType<typeof axios.create> => {
	const config: Record<string, unknown> = {
		timeout: 10000,
		headers: {'Content-Type': 'application/json'},
	}

	// Only add agents on the server side
	if (typeof window === 'undefined') {
		try {
			// eslint-disable-next-line @typescript-eslint/no-var-requires, @typescript-eslint/no-unsafe-assignment -- server-side only
			const {createAgents} = require('./create-agents')
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call -- dynamic require
			const {httpAgent, httpsAgent} = createAgents()
			config.httpAgent = httpAgent
			config.httpsAgent = httpsAgent
		} catch (error) {
			// HTTP agents not available - this is expected in some environments
			// Connection pooling will be disabled but requests will still work
			if (process.env.NODE_ENV === 'development') {
				// eslint-disable-next-line no-console -- intentional debug logging for development troubleshooting
				console.debug(
					'[HTTP Service] HTTP agents not available, continuing without connection pooling:',
					error instanceof Error ? error.message : error
				)
			}
		}
	}

	return axios.create(config)
}

export const http = createHttpClient()
